import { useContext, useRef, useEffect } from 'react';
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	List,
	Skeleton,
	useDisclosure,
	Box,
	Tooltip,
	Icon,
} from 'src/components/ui';
import NavListItem from './NavListItem';
import NestedNavList from './NestedNavList';
import UserContext from 'src/contexts/user/UserContext';
import { IMenuItem } from 'src/services/menu';
import CurrentPlanCard from 'src/components/modals/trial-ending-modal/CurrentPlanCard';
import {
	LEFT_SIDEBAR_WIDTH,
	LEFT_SIDEBAR_WIDTH_COLLAPSED,
	TOP_BAR_HEIGHT,
} from 'src/components/campaigns/utils/constants';
import { useLeftSidebarContext } from 'src/contexts/app/LeftSidebarContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { iconMapper } from 'src/services/iconMapper';

const hasAccess = (item: IMenuItem, planId: string): boolean => {
	if (item.rules && Array.isArray(item.rules) && item.rules.length > 0) {
		return item.rules.some((rule) => rule.allowed_plans.includes(planId));
	}
	return true;
};

const filterNavItems = (items: IMenuItem[], planId: string): IMenuItem[] => {
	return items.reduce((acc: IMenuItem[], item: IMenuItem) => {
		if (hasAccess(item, planId)) {
			const filteredItem = { ...item };
			if (filteredItem.children && filteredItem.children.length > 0) {
				filteredItem.children = filterNavItems(filteredItem.children, planId);
			}
			acc.push(filteredItem);
		}
		return acc;
	}, []);
};

interface ISideBarProps {
	setDrawerOpen?: (open: boolean) => void;
}

function DrawerExample({ setDrawerOpen }: ISideBarProps) {
	const { isOpen, closeSidebar } = useLeftSidebarContext();
	const { navList, isLoading, currentPlan } = useContext(UserContext);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const planId = currentPlan?._id ?? '';
	const filteredNavList = navList ? filterNavItems(navList, planId) : [];
	const drawerRef = useRef<HTMLDivElement>(null);

	const sidebarWidth = LEFT_SIDEBAR_WIDTH;
	const isMobile = () => window.innerWidth < 768;

	const isActiveLink = (link: string) => {
		const [baseLink] = link.split('?');
		return pathname.startsWith(baseLink);
	};

	// Handle click outside on mobile to close sidebar
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Only on mobile and when open
			if (isMobile() && isOpen && drawerRef.current) {
				// Check if click is outside the drawer
				if (!drawerRef.current.contains(event.target as Node)) {
					closeSidebar();
				}
			}
		};

		// Add event listener
		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, closeSidebar]);

	return (
		<Drawer
			isOpen={isOpen}
			placement="left"
			onClose={closeSidebar}
			variant="clickThrough"
			trapFocus={false}
			blockScrollOnMount={false}
		>
			<DrawerContent
				ref={drawerRef}
				bg="white"
				w={sidebarWidth}
				maxW={sidebarWidth}
				marginTop={TOP_BAR_HEIGHT}
				pt="10px"
				boxShadow="0 0px 15px -3px rgba(0, 0, 0, 0.1)"
				transition="width 0.3s ease"
				zIndex={2100}
			>
				<DrawerBody p={1} w="100%" display="flex" flexDirection="column">
					<Box flex="1">
						<List>
							{isLoading ? (
								<>
									{[...Array(5)].map((_, index) => (
										<Skeleton key={index} height="20px" my="4" />
									))}
								</>
							) : filteredNavList.length > 0 ? (
								filteredNavList.map((item: IMenuItem) =>
									item.children && item.children.length > 0 ? (
										<NestedNavList
											key={item._id}
											{...item}
											setDrawerOpen={setDrawerOpen}
											onItemClick={() => {
												if (isMobile()) {
													closeSidebar();
												}
											}}
										/>
									) : (
										<NavListItem
											key={item._id}
											{...item}
											onItemClick={() => {
												if (isMobile()) {
													closeSidebar();
												}
											}}
										/>
									),
								)
							) : (
								<div></div>
							)}
						</List>
					</Box>

					<CurrentPlanCard />
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
}

export default DrawerExample;
